import {
  OUTBOX_BACKOFF_BASIS_MS,
  OUTBOX_BACKOFF_MAX_MS,
  OUTBOX_MAX_VERSUCHE,
  uuidV7,
} from '@uep/shared';
import { db } from './db';
import type { OutboxEintrag, OutboxEntitaet, OutboxOperation } from './typen';

/**
 * Outbox-Queue (PHASENPLAN 1.2).
 *
 * Jede Schreiboperation geht zuerst synchron in IndexedDB und in diese Queue,
 * erst danach ans Backend (CLAUDE.md §3.4). Der `id` ist eine UUID v7 und
 * zugleich Idempotenzschlüssel: Ein wiederholter Versuch schreibt nie doppelt.
 *
 * Reihenfolge: strikt FIFO **je Gruppe**. Eine Gruppe ist üblicherweise ein
 * Protokoll — dessen Anlage muss vor seinen Fotos ankommen. Unterschiedliche
 * Gruppen blockieren sich gegenseitig nicht, sonst hielte ein einzelner
 * hängender Eintrag den ganzen Sync auf.
 */

export interface NeuerEintrag {
  entitaet: OutboxEntitaet;
  operation: OutboxOperation;
  entitaetId: string;
  gruppe?: string;
  nutzlast?: Record<string, unknown>;
}

export const inDieOutbox = async (eintrag: NeuerEintrag): Promise<string> => {
  const id = uuidV7();
  await db.outbox.add({
    id,
    entitaet: eintrag.entitaet,
    operation: eintrag.operation,
    entitaetId: eintrag.entitaetId,
    gruppe: eintrag.gruppe ?? `${eintrag.entitaet}:${eintrag.entitaetId}`,
    nutzlast: eintrag.nutzlast ?? {},
    versuche: 0,
    naechsterVersuchAm: 0,
    letzterFehler: null,
    status: 'offen',
    erstelltAm: new Date().toISOString(),
  });
  return id;
};

/**
 * Backoff: 1s → 2s → 4s … gedeckelt bei 5 Minuten, mit bis zu 20 % Streuung.
 * Die Streuung verhindert, dass alle Geräte nach einem Netzausfall im
 * Gleichtakt wieder zuschlagen.
 */
export const backoffDauer = (versuche: number, streuung = Math.random()): number => {
  const roh = OUTBOX_BACKOFF_BASIS_MS * 2 ** Math.max(0, versuche - 1);
  const gedeckelt = Math.min(roh, OUTBOX_BACKOFF_MAX_MS);
  return Math.round(gedeckelt * (1 + streuung * 0.2));
};

/**
 * Die als Nächstes zu sendenden Einträge — höchstens einer je Gruppe, damit
 * die Reihenfolge innerhalb einer Gruppe garantiert bleibt.
 */
export const naechsteEintraege = async (maxAnzahl = 8): Promise<OutboxEintrag[]> => {
  const jetzt = Date.now();
  const offene = await db.outbox
    .where('status')
    .anyOf('offen', 'fehler')
    .and((eintrag) => eintrag.naechsterVersuchAm <= jetzt)
    .sortBy('id');

  const proGruppe = new Map<string, OutboxEintrag>();
  for (const eintrag of offene) {
    if (!proGruppe.has(eintrag.gruppe)) proGruppe.set(eintrag.gruppe, eintrag);
    if (proGruppe.size >= maxAnzahl) break;
  }
  return [...proGruppe.values()];
};

/**
 * Prüft, ob eine Gruppe blockiert ist: Steht ein älterer Eintrag derselben
 * Gruppe noch aus, darf der jüngere nicht vorgezogen werden.
 */
export const gruppeBlockiert = async (eintrag: OutboxEintrag): Promise<boolean> => {
  const aeltere = await db.outbox
    .where('gruppe')
    .equals(eintrag.gruppe)
    .and((anderer) => anderer.id < eintrag.id && anderer.status !== 'aufgegeben')
    .count();
  return aeltere > 0;
};

export const alsErledigt = async (id: string): Promise<void> => {
  await db.outbox.delete(id);
};

export const alsLaufend = async (id: string): Promise<void> => {
  await db.outbox.update(id, { status: 'laeuft' });
};

export const alsFehlgeschlagen = async (
  eintrag: OutboxEintrag,
  grund: string,
  dauerhaft = false,
): Promise<void> => {
  const versuche = eintrag.versuche + 1;
  const aufgegeben = dauerhaft || versuche >= OUTBOX_MAX_VERSUCHE;

  await db.outbox.update(eintrag.id, {
    versuche,
    status: aufgegeben ? 'aufgegeben' : 'fehler',
    letzterFehler: grund.slice(0, 500),
    naechsterVersuchAm: aufgegeben ? Number.MAX_SAFE_INTEGER : Date.now() + backoffDauer(versuche),
  });
};

/** Setzt aufgegebene Einträge zurück — der Nutzer entscheidet, es erneut zu versuchen. */
export const erneutVersuchen = async (id?: string): Promise<number> => {
  const aenderung = {
    status: 'offen' as const,
    naechsterVersuchAm: 0,
    versuche: 0,
    letzterFehler: null,
  };
  if (id) {
    await db.outbox.update(id, aenderung);
    return 1;
  }
  return db.outbox.where('status').equals('aufgegeben').modify(aenderung);
};

/** Setzt beim Start hängengebliebene „läuft"-Einträge zurück. */
export const laufendeZuruecksetzen = async (): Promise<number> =>
  db.outbox.where('status').equals('laeuft').modify({ status: 'offen', naechsterVersuchAm: 0 });

export interface OutboxZaehler {
  offen: number;
  laeuft: number;
  fehler: number;
  aufgegeben: number;
  gesamt: number;
}

export const outboxZaehler = async (): Promise<OutboxZaehler> => {
  const alle = await db.outbox.toArray();
  const zaehle = (status: OutboxEintrag['status']): number =>
    alle.filter((eintrag) => eintrag.status === status).length;

  return {
    offen: zaehle('offen'),
    laeuft: zaehle('laeuft'),
    fehler: zaehle('fehler'),
    aufgegeben: zaehle('aufgegeben'),
    gesamt: alle.length,
  };
};

export interface OutboxProblem {
  id: string;
  entitaet: OutboxEntitaet;
  operation: OutboxOperation;
  status: OutboxEintrag['status'];
  versuche: number;
  letzterFehler: string | null;
  /** Wartet der Eintrag noch im Backoff, steht hier der Zeitpunkt. */
  wartetBis: string | null;
}

/** Rangfolge für die Anzeige: Was hängt, steht oben. */
const RANG: Record<OutboxEintrag['status'], number> = {
  aufgegeben: 0,
  fehler: 1,
  laeuft: 2,
  offen: 3,
};

/**
 * Die hängenden Einträge samt Grund — für den Sync-Dialog.
 *
 * Ohne diese Liste sieht man nur „3 Änderungen warten“ und hat keine
 * Handhabe: Ein Rechtefehler sieht dann genauso aus wie ein Funkloch.
 */
export const outboxProbleme = async (maxAnzahl = 5): Promise<OutboxProblem[]> => {
  const alle = await db.outbox.toArray();
  const jetzt = Date.now();

  return alle
    .sort((a, b) => RANG[a.status] - RANG[b.status] || a.id.localeCompare(b.id))
    .slice(0, maxAnzahl)
    .map((eintrag) => ({
      id: eintrag.id,
      entitaet: eintrag.entitaet,
      operation: eintrag.operation,
      status: eintrag.status,
      versuche: eintrag.versuche,
      letzterFehler: eintrag.letzterFehler,
      wartetBis:
        eintrag.status === 'fehler' && eintrag.naechsterVersuchAm > jetzt
          ? new Date(eintrag.naechsterVersuchAm).toISOString()
          : null,
    }));
};

/** Anzahl Protokolle mit noch ausstehenden Änderungen. */
export const wartendeProtokolle = async (): Promise<number> => {
  const eintraege = await db.outbox.where('status').notEqual('aufgegeben').toArray();
  const protokolle = new Set(
    eintraege
      .filter((eintrag) => eintrag.gruppe.startsWith('Protocol:'))
      .map((eintrag) => eintrag.gruppe),
  );
  return protokolle.size;
};

export const outboxLeeren = async (): Promise<void> => {
  await db.outbox.clear();
};
